import React, { useCallback, useState } from 'react'

import type { CellContext } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import NextLink from 'next/link'
import {
  HiEllipsisHorizontal,
  HiOutlineEye,
  HiOutlinePencilSquare,
  HiOutlineTrash,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import IconButton from '@/components/common/IconButtonV2'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'
import { Skeleton } from '@/components/common/Skeleton'
import { useCatalogEntrySchema } from '@/components/library-v2/catalog/hooks'
import { DeleteEntriesDialog } from '@/components/library-v2/catalog/ui/DeleteEntriesDialog'
import { EditEntryDrawer } from '@/components/library-v2/catalog/ui/EditEntryDialog'
import { Link } from '@/components/library-v2/ui/Link'
import { DataCellLayout } from '@/components/library-v2/ui/Table'

import { COLOR_CLASSNAMES } from '@/constants/color'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { EntityCatalogEntry } from '@/types/library/Entity'

import { usePermissionLoader } from '@/services/permission-loader/context'

export const ActiveCell: React.FC<CellContext<EntityCatalogEntry, unknown>> = ({
  row,
}) => {
  const entry = row.original
  return (
    <DataCellLayout className="py-1.5">
      <Badge
        variant={entry.archived ? 'destructive' : 'positive'}
        className="text-[13px]"
      >
        {entry.archived ? 'Inactive' : 'Active'}
      </Badge>
    </DataCellLayout>
  )
}

export const EntryCell: React.FC<CellContext<EntityCatalogEntry, unknown>> = ({
  row,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const entry = row.original
  return (
    <DataCellLayout className="max-w-full truncate">
      <Link
        href={`/orgs/${activeOrgId}/library/catalog/entries/${entry.id}`}
        className="font-medium text-sm truncate"
      >
        {entry.name}
      </Link>
    </DataCellLayout>
  )
}

export const SchemaCell: React.FC<CellContext<EntityCatalogEntry, unknown>> = ({
  row,
}) => {
  const { data: schema, isLoading } = useCatalogEntrySchema({
    schemaId: row.original.extended_schema_id ?? '',
  })

  if (isLoading) {
    return (
      <DataCellLayout>
        <Skeleton className="h-3 w-16 rounded-full" />
      </DataCellLayout>
    )
  }

  if (!schema) {
    return <EmptyCell />
  }

  const classNames = COLOR_CLASSNAMES[schema.color]

  return (
    <DataCellLayout>
      <Badge
        variant="outline"
        className={twMerge(
          'font-medium px-2 text-xs rounded-full whitespace-nowrap',
          classNames,
        )}
      >
        {schema.name}
      </Badge>
    </DataCellLayout>
  )
}

// export const PriceCell: React.FC<CellContext<EntityCatalogEntry, unknown>> = ({
//   row,
// }) => {
//   const entry = row.original
//   const hasPrice =
//     typeof entry.price === 'number' && Number.isFinite(entry.price)
//   const priceText = hasPrice ? entry.price.toLocaleString() : '—'
//   const unit = entry.unit_of_measure || ''

//   return (
//     <DataCellLayout>
//       <div className="font-medium text-primary-900 dark:text-primary-100 text-sm">
//         {hasPrice ? `$${priceText}` : '—'}
//       </div>
//       {unit ? (
//         <div className="text-sm text-primary-500 dark:text-primary-400">
//           /{unit}
//         </div>
//       ) : null}
//     </DataCellLayout>
//   )
// }

export const LastModifiedCell: React.FC<
  CellContext<EntityCatalogEntry, unknown>
> = ({ row }) => {
  const dt = row.original.last_modified
  return (
    <DataCellLayout className="py-1.5">
      <span className="text-sm text-primary-500 dark:text-primary-400">
        {dt ? formatDistanceToNow(new Date(dt), { addSuffix: true }) : '-'}
      </span>
    </DataCellLayout>
  )
}

export const EmptyCell: React.FC = () => {
  return (
    <DataCellLayout className="py-1.5">
      <span className="text-sm text-primary-700 dark:text-primary-300">—</span>
    </DataCellLayout>
  )
}

export const ActionsCell: React.FC<
  CellContext<EntityCatalogEntry, unknown>
> = ({ row }) => {
  const { activeOrgId } = useActiveOrgId()
  const permissionLoader = usePermissionLoader()
  const entry = row.original

  const [showEditDrawer, setShowEditDrawer] = useState<boolean>(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false)

  const handleDelete = useCallback(() => {
    setDeleteDialogOpen(true)
  }, [setDeleteDialogOpen])

  const handleEdit = useCallback(() => {
    setEditDrawerOpen(true)
    setShowEditDrawer(true)
  }, [setEditDrawerOpen, setShowEditDrawer])

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false)
  }, [setDeleteDialogOpen])

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      setEditDrawerOpen(open)
      if (!open) {
        // give time for the animation to complete before hiding the edit drawer
        setTimeout(() => {
          setShowEditDrawer(false)
        }, 300)
      } else {
        setShowEditDrawer(true)
      }
    },
    [setEditDrawerOpen, setShowEditDrawer],
  )

  // Pre-warm permission cache on hover to avoid flicker
  const handleMouseEnter = useCallback(() => {
    if (permissionLoader && entry.id) {
      permissionLoader.loadMany([
        {
          resourceType: 'entity',
          resourceId: entry.id,
          permission: 'write',
        },
      ])
    }
  }, [permissionLoader, entry.id])

  return (
    <div
      className={twMerge('flex items-center justify-end gap-1 pr-3')}
      onMouseEnter={handleMouseEnter}
    >
      {showEditDrawer && (
        <EditEntryDrawer
          entry={entry}
          open={editDrawerOpen}
          onOpenChange={handleDrawerOpenChange}
        />
      )}
      {deleteDialogOpen && (
        <DeleteEntriesDialog
          schemaId={entry.extended_schema_id ?? undefined}
          catalogEntryIds={[entry.id]}
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
          onDelete={handleCloseDeleteDialog}
        />
      )}
      <ResourcePermissionGate
        resourceType="entity"
        resourceId={entry.id}
        permission="write"
        fallback="disable"
      >
        <IconButton
          tooltip="Edit"
          onClick={handleEdit}
          tooltipContentProps={{ side: 'left' }}
        >
          <HiOutlinePencilSquare
            size={16}
            className="flex-shrink-0 text-primary-700 dark:text-primary-300"
          />
        </IconButton>
      </ResourcePermissionGate>
      <DropdownMenu>
        <DropdownMenuTrigger asChild={true}>
          <IconButton
            tooltip="More Actions"
            tooltipProps={{ delayDuration: 250 }}
          >
            <HiEllipsisHorizontal
              size={18}
              className="flex-shrink-0 text-primary-700 dark:text-primary-300 stroke-1"
            />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <ResourcePermissionGate
            resourceType="entity"
            resourceId={entry.id}
            permission="write"
            fallback="disable"
            asChild
          >
            <DropdownMenuItem onClick={handleEdit}>
              <HiOutlinePencilSquare
                size={16}
                className="flex-shrink-0 text-primary-700 dark:text-primary-300 mr-2"
              />
              <span>Edit</span>
            </DropdownMenuItem>
          </ResourcePermissionGate>
          <DropdownMenuItem asChild={true}>
            <NextLink
              href={`/orgs/${activeOrgId}/library/catalog/entries/${entry.id}`}
              className="flex items-center"
            >
              <HiOutlineEye
                size={16}
                className="flex-shrink-0 text-primary-700 dark:text-primary-300 mr-2"
              />
              <span>View</span>
            </NextLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <ResourcePermissionGate
            resourceType="entity"
            resourceId={entry.id}
            permission="write"
            fallback="disable"
            asChild
          >
            <DropdownMenuItem
              onClick={handleDelete}
              className="text-red-600 dark:text-red-500"
            >
              <HiOutlineTrash
                size={16}
                className="flex-shrink-0 text-red-600 dark:text-red-500 mr-2"
              />
              <span>Delete</span>
            </DropdownMenuItem>
          </ResourcePermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
